import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../App';

export default function PortfolioCommercial() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.scroll-reveal').forEach((el) => el.classList.add('animate-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('animate-visible'); }); },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`min-h-screen ${isDarkMode ? 'bg-[#111111] text-white' : 'bg-[#faf9f7] text-[#2a2a2a]'} transition-colors duration-500`}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        .scroll-reveal {
          opacity: 0;
          transform: translateY(36px);
          transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal.animate-visible { opacity: 1; transform: translateY(0); }
        .scroll-reveal-delay-1 { transition-delay: 0.12s; }
        .scroll-reveal-delay-2 { transition-delay: 0.24s; }
        .scroll-reveal-delay-3 { transition-delay: 0.36s; }
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .afu { animation: fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .afu-1 { animation: fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards; opacity: 0; }
        .afu-2 { animation: fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards; opacity: 0; }
        .afu-3 { animation: fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) 0.65s forwards; opacity: 0; }
        .afu-4 { animation: fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) 0.9s forwards; opacity: 0; }
        @keyframes line-in {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        .line-animate {
          transform-origin: left;
          animation: line-in 1.6s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards;
          transform: scaleX(0);
        }
        .photo-slot {
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .photo-slot:hover { transform: scale(1.025); }
        .video-frame {
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .video-frame:hover { transform: scale(1.008); }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.15); opacity: 0.2; }
          100% { transform: scale(1); opacity: 0.5; }
        }
        .pulse-ring { animation: pulse-ring 2.5s ease-in-out infinite; }
      `}</style>

      {/* ─── Top Bar ──────────────────────────────── */}
      <div className="fixed top-4 right-4 md:top-8 md:right-8 flex items-center gap-3 md:gap-4 z-50">
        <button
          onClick={toggleDarkMode}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors cursor-pointer`}
        >
          <i className={`ri-${isDarkMode ? 'moon' : 'sun'}-line text-lg md:text-xl`}></i>
        </button>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors cursor-pointer`}
        >
          <i className="ri-menu-line text-lg md:text-xl"></i>
        </button>
      </div>

      {/* ─── Slide-out Menu ───────────────────────── */}
      <div
        className={`fixed top-0 right-0 h-full w-72 md:w-80 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#f5f4f2]'} shadow-2xl transform transition-transform duration-500 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="p-6 md:p-8">
          <button
            onClick={() => setMenuOpen(false)}
            className={`absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors cursor-pointer`}
          >
            <i className="ri-close-line text-lg md:text-xl"></i>
          </button>
          <nav className="mt-16 md:mt-20 space-y-5 md:space-y-6">
            <Link to="/home" onClick={() => setMenuOpen(false)} className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors`}>Home</Link>
            <a href="https://order.cinestate.ca/booking/new" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors`}>Listing Packages</a>
            <Link to="/packages" onClick={() => setMenuOpen(false)} className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors`}>Content Packages</Link>
            <Link to="/staging" onClick={() => setMenuOpen(false)} className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors`}>Home Staging</Link>
            <div>
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={`flex items-center justify-between w-full text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors cursor-pointer`}
              >
                <span>Portfolio</span>
                <i className={`${portfolioOpen ? 'ri-subtract-line' : 'ri-add-line'} text-xs`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${portfolioOpen ? 'max-h-24 mt-3' : 'max-h-0'}`}>
                <div className="ml-4 space-y-3">
                  <Link to="/portfolio" onClick={() => setMenuOpen(false)} className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#2a2a2a]/55 hover:text-[#2a2a2a]'} transition-colors`}>Residential</Link>
                  <Link to="/portfolio/commercial" onClick={() => setMenuOpen(false)} className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors`}>Commercial</Link>
                </div>
              </div>
            </div>
            <Link to="/home#contact" onClick={() => setMenuOpen(false)} className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'} transition-colors`}>Contact</Link>
          </nav>
          <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 flex gap-3 md:gap-4">
            {[
              { icon: 'ri-instagram-line', href: 'https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0' },
              { icon: 'ri-youtube-line', href: 'https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8' },
              { icon: 'ri-tiktok-line', href: 'https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY' },
              { icon: 'ri-facebook-fill', href: 'https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0' },
            ].map((s) => (
              <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer"
                className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/60 hover:text-white hover:border-white' : 'border-[#2a2a2a]/20 text-[#2a2a2a]/60 hover:text-[#2a2a2a] hover:border-[#2a2a2a]'} transition-colors`}>
                <i className={`${s.icon} text-sm md:text-base`}></i>
              </a>
            ))}
          </div>
        </div>
      </div>
      {menuOpen && <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setMenuOpen(false)}></div>}

      {/* ─── Logo ─────────────────────────────────── */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-40">
        <Link to="/" className="block">
          <img
            src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
            alt="CINÉSTATE"
            className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
          />
        </Link>
      </div>

      {/* ═══════════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════════ */}
      <section className={`relative min-h-screen flex flex-col justify-center items-center px-4 overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'}`}>
        <div className="absolute inset-0">
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)'} 1px, transparent 1px)`, backgroundSize: '80px 80px' }}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto w-full text-center">
          {/* Tab switcher */}
          <div className="afu flex items-center justify-center gap-6 mb-14">
            <Link to="/portfolio" className={`text-[10px] sm:text-xs tracking-[0.35em] uppercase pb-1.5 border-b border-transparent ${isDarkMode ? 'text-white/35 hover:text-white/70' : 'text-[#2a2a2a]/35 hover:text-[#2a2a2a]/70'} transition-colors`}>
              Residential
            </Link>
            <div className={`w-px h-3 ${isDarkMode ? 'bg-white/15' : 'bg-[#2a2a2a]/15'}`}></div>
            <span className={`text-[10px] sm:text-xs tracking-[0.35em] uppercase pb-1.5 border-b ${isDarkMode ? 'text-white border-white/60' : 'text-[#2a2a2a] border-[#2a2a2a]/60'}`}>
              Commercial
            </span>
          </div>

          <h1 className="afu-2 font-normal leading-[1.04] tracking-wide mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.4rem, 6vw, 5.5rem)' }}>
            Elevating Commercial<br />Real Estate
          </h1>

          {/* Animated line */}
          <div className="flex justify-center mb-10">
            <div className={`line-animate h-px w-28 ${isDarkMode ? 'bg-[#d4c5b0]/50' : 'bg-[#8a7d6d]/50'}`}></div>
          </div>

          <p className="afu-3 text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-xl mx-auto mb-14" style={{ fontFamily: "'Cormorant Garamond', serif", color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(42,42,42,0.5)' }}>
            Aerial perspectives. AI-driven motion. Precision floor plans. Every service crafted to make commercial listings impossible to ignore.
          </p>

          {/* Scroll to services */}
          <div className="afu-4 flex flex-col items-center gap-3">
            <span className={`text-[9px] tracking-[0.35em] uppercase ${isDarkMode ? 'text-white/25' : 'text-[#2a2a2a]/25'}`}>Our Services</span>
            <div className={`w-px h-10 ${isDarkMode ? 'bg-gradient-to-b from-white/25 to-transparent' : 'bg-gradient-to-b from-[#2a2a2a]/25 to-transparent'}`}></div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SERVICE 01 — AERIAL DRONE VIDEO + AI MOTION GRAPHICS
      ═══════════════════════════════════════════════════════ */}
      <section className={`py-20 sm:py-28 md:py-36 px-4 sm:px-8 md:px-16 ${isDarkMode ? 'bg-[#111111]' : 'bg-[#faf9f7]'}`}>
        <div className="max-w-7xl mx-auto">

          {/* Header row */}
          <div className="scroll-reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 md:mb-20">
            <div>
              <h2 className="font-normal leading-[1.05] tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3.2rem)' }}>
                Ground & Aerial Drone Video<br />
                <span className={isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}>+ Advanced AI Motion Graphics</span>
              </h2>
            </div>
            <p className={`text-sm sm:text-base md:text-lg font-light leading-relaxed max-w-sm ${isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Cinematic drone footage fused with cutting-edge AI motion design — built to captivate buyers and position every commercial property as a landmark.
            </p>
          </div>

          {/* ── Row 1: Three Vertical (Shorts) ── */}
          <div className="scroll-reveal scroll-reveal-delay-1 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mb-4 md:mb-5">
            {[
              { id: 'cZ4DmeKY7H8', label: 'Short — Commercial Reel I' },
              { id: 'UHGkDIdRpwY', label: 'Short — Commercial Reel II' },
              { id: 'cgyD_xtdmgM', label: 'Short — Commercial Reel III' },
            ].map((v) => (
              <div key={v.id} className="flex flex-col gap-2">
                <div
                  className={`relative overflow-hidden video-frame`}
                  style={{ aspectRatio: '9/16', borderRadius: '2px' }}
                >
                  <div className={`absolute top-0 left-0 w-5 h-5 border-t border-l z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <div className={`absolute top-0 right-0 w-5 h-5 border-t border-r z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <div className={`absolute bottom-0 left-0 w-5 h-5 border-b border-l z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <div className={`absolute bottom-0 right-0 w-5 h-5 border-b border-r z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <iframe
                    src={`https://www.youtube.com/embed/${v.id}?rel=0&modestbranding=1`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={v.label}
                  ></iframe>
                </div>
                <span className={`text-[9px] tracking-[0.28em] uppercase px-0.5 ${isDarkMode ? 'text-white/25' : 'text-[#2a2a2a]/25'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                  {v.label}
                </span>
              </div>
            ))}
          </div>

          {/* ── Row 2: Two Horizontal ── */}
          <div className="scroll-reveal scroll-reveal-delay-2 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {[
              { id: 'SccyPwPd5q4', label: 'Feature — Aerial & AI Composite' },
              { id: 'Ynlbq4Ruj60', label: 'Feature — Commercial Showcase' },
            ].map((v) => (
              <div key={v.id} className="flex flex-col gap-2">
                <div
                  className={`relative overflow-hidden video-frame`}
                  style={{ aspectRatio: '16/9', borderRadius: '2px' }}
                >
                  <div className={`absolute top-0 left-0 w-6 h-6 border-t border-l z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <div className={`absolute top-0 right-0 w-6 h-6 border-t border-r z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <div className={`absolute bottom-0 left-0 w-6 h-6 border-b border-l z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <div className={`absolute bottom-0 right-0 w-6 h-6 border-b border-r z-10 ${isDarkMode ? 'border-[#d4c5b0]/40' : 'border-[#8a7d6d]/40'}`}></div>
                  <iframe
                    src={`https://www.youtube.com/embed/${v.id}?rel=0&modestbranding=1`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={v.label}
                  ></iframe>
                </div>
                <span className={`text-[9px] tracking-[0.28em] uppercase px-0.5 ${isDarkMode ? 'text-white/25' : 'text-[#2a2a2a]/25'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                  {v.label}
                </span>
              </div>
            ))}
          </div>


        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/6' : 'bg-[#2a2a2a]/6'}`}></div>

      {/* ═══════════════════════════════════════════════════════
          SERVICE 02 — AERIAL DRONE PHOTOS
      ═══════════════════════════════════════════════════════ */}
      <section className={`py-20 sm:py-28 md:py-36 px-4 sm:px-8 md:px-16 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#f5f3f0]'}`}>
        <div className="max-w-7xl mx-auto">

          {/* Header row */}
          <div className="scroll-reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 md:mb-20">
            <div>
              <h2 className="font-normal leading-[1.05] tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3.2rem)' }}>
                Ground & Aerial Drone<br />
                <span className={isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}>Photography</span>
              </h2>
            </div>
            <p className={`text-sm sm:text-base md:text-lg font-light leading-relaxed max-w-sm ${isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Breathtaking bird's-eye imagery that reveals scale, context, and surroundings — perspectives no ground-level shot can achieve.
            </p>
          </div>

          {/* Photo Grid — aerial stock images */}
          <div className="scroll-reveal scroll-reveal-delay-1 grid grid-cols-12 gap-3 md:gap-4">
            {/* Large main slot */}
            <div className="col-span-12 md:col-span-8 relative overflow-hidden group cursor-pointer photo-slot" style={{ height: 'clamp(240px, 38vw, 540px)', borderRadius: '2px' }}>
              <img
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/0b6b64e24789d7cd3e1b49fc89a71d7e.jpeg"
                alt="Aerial commercial photography main shot"
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/20' : 'border-[#8a7d6d]/25'}`}></div>
            </div>

            {/* Tall side slots */}
            <div className="col-span-12 md:col-span-4 flex flex-col gap-3 md:gap-4">
              <div className="flex-1 relative overflow-hidden group cursor-pointer photo-slot" style={{ height: 'clamp(120px, 18vw, 260px)', borderRadius: '2px' }}>
                <img
                  src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/86a7f3c253d62fa63d2a9ee16929c513.png"
                  alt="Aerial office building top view"
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/20' : 'border-[#8a7d6d]/25'}`}></div>
              </div>
              <div className="flex-1 relative overflow-hidden group cursor-pointer photo-slot" style={{ height: 'clamp(120px, 18vw, 260px)', borderRadius: '2px' }}>
                <img
                  src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/cea35d633efafc371f57b164621ae0b6.jpeg"
                  alt="Aerial hotel resort commercial view"
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/20' : 'border-[#8a7d6d]/25'}`}></div>
              </div>
            </div>

            {/* Bottom row — three equal slots */}
            <div className="col-span-12 sm:col-span-4 relative overflow-hidden group cursor-pointer photo-slot" style={{ height: 'clamp(120px, 15vw, 220px)', borderRadius: '2px' }}>
              <img
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/32432e6269fead92f7acc741a64fe86a.jpeg"
                alt="Aerial retail plaza commercial"
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/20' : 'border-[#8a7d6d]/25'}`}></div>
            </div>
            <div className="col-span-12 sm:col-span-4 relative overflow-hidden group cursor-pointer photo-slot" style={{ height: 'clamp(120px, 15vw, 220px)', borderRadius: '2px' }}>
              <img
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/ca3b45315f9388f555f73eeaf9b600f5.png"
                alt="Aerial warehouse industrial commercial"
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/20' : 'border-[#8a7d6d]/25'}`}></div>
            </div>
            <div className="col-span-12 sm:col-span-4 relative overflow-hidden group cursor-pointer photo-slot" style={{ height: 'clamp(120px, 15vw, 220px)', borderRadius: '2px' }}>
              <img
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/c253b94f9aaf9df9305229306e0ab98f.jpeg"
                alt="Aerial twilight commercial development"
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500"></div>
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/20' : 'border-[#8a7d6d]/25'}`}></div>
            </div>
          </div>


        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/6' : 'bg-[#2a2a2a]/6'}`}></div>

      {/* ═══════════════════════════════════════════════════════
          SERVICE 03 — FLOOR PLAN & ROOM MEASUREMENTS
      ═══════════════════════════════════════════════════════ */}
      <section className={`py-20 sm:py-28 md:py-36 px-4 sm:px-8 md:px-16 ${isDarkMode ? 'bg-[#111111]' : 'bg-[#faf9f7]'}`}>
        <div className="max-w-7xl mx-auto">

          {/* Header row */}
          <div className="scroll-reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 md:mb-20">
            <div>
              <h2 className="font-normal leading-[1.05] tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3.2rem)' }}>
                Floor Plan<br />
                <span className={isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}>& Room Measurements</span>
              </h2>
            </div>
            <p className={`text-sm sm:text-base md:text-lg font-light leading-relaxed max-w-sm ${isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Precise architectural floor plans with measured room dimensions — giving buyers the clarity and confidence to act.
            </p>
          </div>

          {/* Floor plan image + details */}
          <div className="scroll-reveal scroll-reveal-delay-1 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">

            {/* Left — Floor plan image */}
            <div
              className="relative overflow-hidden cursor-pointer group photo-slot"
              style={{ aspectRatio: '4/3', borderRadius: '2px' }}
            >
              <img
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/ed2f3f10d49a5fe2c60dfadb2a5e17dd.jpeg"
                alt="Commercial floor plan sample with room measurements"
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              {/* Subtle dark overlay for dark mode */}
              {isDarkMode && <div className="absolute inset-0 bg-black/30"></div>}
              {/* Corner accents */}
              <div className={`absolute top-0 left-0 w-6 h-6 border-t border-l z-10 ${isDarkMode ? 'border-[#d4c5b0]/50' : 'border-[#8a7d6d]/50'}`}></div>
              <div className={`absolute top-0 right-0 w-6 h-6 border-t border-r z-10 ${isDarkMode ? 'border-[#d4c5b0]/50' : 'border-[#8a7d6d]/50'}`}></div>
              <div className={`absolute bottom-0 left-0 w-6 h-6 border-b border-l z-10 ${isDarkMode ? 'border-[#d4c5b0]/50' : 'border-[#8a7d6d]/50'}`}></div>
              <div className={`absolute bottom-0 right-0 w-6 h-6 border-b border-r z-10 ${isDarkMode ? 'border-[#d4c5b0]/50' : 'border-[#8a7d6d]/50'}`}></div>
              {/* Hover border */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none border ${isDarkMode ? 'border-[#d4c5b0]/25' : 'border-[#8a7d6d]/30'}`}></div>
              {/* Label badge */}
              <div className={`absolute bottom-4 left-4 z-10 px-3 py-1.5 text-[9px] tracking-[0.25em] uppercase ${isDarkMode ? 'bg-[#0d0d0d]/80 text-white/60' : 'bg-white/80 text-[#2a2a2a]/60'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                Floor Plan Sample
              </div>
            </div>

            {/* Right — Details */}
            <div className="space-y-8 md:space-y-10">
              {[
                {
                  icon: 'ri-ruler-line',
                  title: 'Precise Room Dimensions',
                  desc: 'Every room meticulously measured and labelled — square footage, ceiling heights, and key structural elements all documented with precision.',
                },
                {
                  icon: 'ri-building-line',
                  title: 'Multi-Floor Support',
                  desc: 'From single-level retail to multi-storey commercial towers — we produce clear, layered floor plans for any building complexity.',
                },
                {
                  icon: 'ri-file-pdf-line',
                  title: 'Digital + Print Ready',
                  desc: 'Delivered in high-res PDF and editable formats, ready for MLS submissions, brochures, and marketing presentations.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`scroll-reveal scroll-reveal-delay-${i + 1} flex items-start gap-5`}
                >
                  <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center border ${isDarkMode ? 'border-white/12 text-[#d4c5b0]' : 'border-[#2a2a2a]/12 text-[#8a7d6d]'}`}>
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm md:text-base font-normal tracking-wide uppercase mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{item.title}</h3>
                    <p className={`text-sm md:text-base font-light leading-relaxed ${isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.desc}</p>
                  </div>
                </div>
              ))}


            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          CTA
      ═══════════════════════════════════════════════════════ */}
      <section className={`py-20 sm:py-28 md:py-36 px-4 sm:px-8 md:px-16 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#ebe8e4]'}`}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="scroll-reveal text-[10px] sm:text-xs tracking-[0.35em] uppercase mb-5 text-[#d4c5b0]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Ready to Get Started?
          </p>
          <h2 className="scroll-reveal scroll-reveal-delay-1 font-normal leading-[1.08] tracking-wide mb-7" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4vw, 3.8rem)' }}>
            Bring Your Commercial<br />Listing to Life
          </h2>
          <p className={`scroll-reveal scroll-reveal-delay-2 text-sm sm:text-base md:text-lg font-light leading-relaxed mb-12 max-w-lg mx-auto ${isDarkMode ? 'text-white/45' : 'text-[#2a2a2a]/45'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Let's discuss your property and build a content strategy that drives attention, builds trust, and accelerates your sale.
          </p>
          <div className="scroll-reveal scroll-reveal-delay-3 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/home#contact"
              className={`inline-block px-10 py-4 ${isDarkMode ? 'bg-white text-[#0d0d0d] hover:bg-white/90' : 'bg-[#2a2a2a] text-white hover:bg-[#2a2a2a]/90'} text-xs md:text-sm tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Get In Touch
            </Link>
            <a
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-10 py-4 border ${isDarkMode ? 'border-white/30 text-white hover:border-white' : 'border-[#2a2a2a]/30 text-[#2a2a2a] hover:border-[#2a2a2a]'} text-xs md:text-sm tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Book Now
            </a>
          </div>
        </div>
      </section>

      {/* ─── Footer ───────────────────────────────── */}
      <footer className={`border-t ${isDarkMode ? 'border-white/10 bg-[#111111]' : 'border-[#2a2a2a]/10 bg-[#faf9f7]'}`}>
        <div className="px-4 sm:px-8 md:px-16 py-10 sm:py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-6 sm:gap-8 md:gap-10 mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                <Link to="/" className="block">
                  <img
                    src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
                    alt="CINÉSTATE"
                    className={`h-4 sm:h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
                  />
                </Link>
                <div className="flex gap-2 sm:gap-3 md:gap-4">
                  {[
                    { icon: 'ri-instagram-line', href: 'https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0' },
                    { icon: 'ri-youtube-line', href: 'https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8' },
                    { icon: 'ri-tiktok-line', href: 'https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY' },
                    { icon: 'ri-facebook-fill', href: 'https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0' },
                  ].map((s) => (
                    <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer"
                      className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors cursor-pointer`}>
                      <i className={`${s.icon} text-sm md:text-base`}></i>
                    </a>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-5 md:gap-8">
                <a href="https://order.cinestate.ca/booking/new" target="_blank" rel="noopener noreferrer" className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors font-normal`}>Listing Packages</a>
                <Link to="/packages" className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors font-normal`}>Content Packages</Link>
                <Link to="/staging" className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors font-normal`}>Home Staging</Link>
                <Link to="/portfolio" className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors font-normal`}>Portfolio</Link>
                <Link to="/home#contact" className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-[#2a2a2a]/60 hover:text-[#2a2a2a]'} transition-colors font-normal`}>Contact</Link>
              </div>
            </div>
            <div className={`w-full h-px ${isDarkMode ? 'bg-white/8' : 'bg-[#2a2a2a]/8'} mb-4 sm:mb-6 md:mb-8`}></div>
            <div className={`flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4 text-[10px] sm:text-xs md:text-sm ${isDarkMode ? 'text-white/40' : 'text-[#2a2a2a]/40'}`}>
              <p className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>© 2025 Cinéstate. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" className={`${isDarkMode ? 'hover:text-white' : 'hover:text-[#2a2a2a]'} transition-colors font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
